// #kinship-quiz-unification — chain pool for RelationQuizV2.
//
// Per-level chain enumeration + self-loop filtering. Pure
// functions, no engine calls. The component fetches labels via
// useKinshipLabels and passes the resulting Map<chainKey, label>
// to `filterSelfLoops` to drop chains whose engine result is
// "self" — those resolve back to ego and aren't useful quiz
// answers.
//
// Levels 1+2 ship in commit 2; levels 3-6 in subsequent commits.

import type { QuizAtom } from './quizNames'

export type Chain = QuizAtom[]

/**
 * Stable key for cache lookups + recent-chain dedup. Matches
 * useKinshipLabels' chainKey helper (pipe-joined).
 */
export function chainKey(chain: Chain): string {
  return chain.join('|')
}

// Level 1 — direct relations. 6 chains, all length 1.
export const LEVEL_1_CHAINS: readonly Chain[] = [
  ['Father'], ['Mother'], ['Brother'], ['Sister'], ['Son'], ['Daughter'],
]

// Level 2 — two-hop ascending. First atom ∈ {Father, Mother},
// second ∈ all 6. 2 × 6 = 12 candidate chains. The component
// filters out self-loops at runtime (e.g. [Father, Son] → "self"
// for male ego).
const ALL_ATOMS: QuizAtom[] = ['Father', 'Mother', 'Son', 'Daughter', 'Brother', 'Sister']
const FIRST_ATOMS_L2 = ['Father', 'Mother'] as const

export const LEVEL_2_CANDIDATES: readonly Chain[] = FIRST_ATOMS_L2
  .flatMap((first) => ALL_ATOMS.map<Chain>((second) => [first as QuizAtom, second]))

// Level 3 — sibling-first chains. [Brother|Sister, Son|Daughter].
// 4 chains, all ego-gender-dependent (cross vs parallel
// classification flips with ego). This is the level where the
// quiz teaches ego-gender awareness — same chain, different
// answer per ego.
const SIB_ATOMS_L3 = ['Brother', 'Sister'] as const
const DESC_ATOMS_L3 = ['Son', 'Daughter'] as const

export const LEVEL_3_CANDIDATES: readonly Chain[] = SIB_ATOMS_L3
  .flatMap((sib) => DESC_ATOMS_L3.map<Chain>((desc) => [sib as QuizAtom, desc as QuizAtom]))

// Level 4 — three-hop ascending (gen+2). Pattern [F|M, F|M, F|M|B|S].
// 2 × 2 × 4 = 16 candidate chains. All ascending → engine never
// returns "self" for these, but filterSelfLoops is still applied
// defensively.
const PARENT_ATOMS = ['Father', 'Mother'] as const
const THIRD_ATOMS_L4 = ['Father', 'Mother', 'Brother', 'Sister'] as const

export const LEVEL_4_CANDIDATES: readonly Chain[] = PARENT_ATOMS
  .flatMap((a) => PARENT_ATOMS
    .flatMap((b) => THIRD_ATOMS_L4
      .map<Chain>((c) => [a as QuizAtom, b as QuizAtom, c as QuizAtom])))

// Level 5 — descent-then-ascent + terminal sibling.
// Pattern [Son|Daughter, Mother|Father, Brother|Sister]. 2×2×2 = 8
// candidate chains. Engine fix a0cea07 (#kinship-engine-step-half-
// collapse-and-descent-ascent) handles the [Son|Daughter, Mother|
// Father] prefix rewrite to WIFE/HUSBAND/ego, so the terminal sibling
// resolves correctly per ego gender. This is THE pedagogical level —
// the same chain produces dramatically different answers for male
// vs female ego (e.g. [Son, Mother, Brother] male → బావ/బావమరిది;
// female → అన్న/తమ్ముడు).
const DESC_L5 = ['Son', 'Daughter'] as const
const ASC_L5  = ['Mother', 'Father'] as const
const SIB_L5  = ['Brother', 'Sister'] as const

export const LEVEL_5_CANDIDATES: readonly Chain[] = DESC_L5
  .flatMap((a) => ASC_L5
    .flatMap((b) => SIB_L5
      .map<Chain>((c) => [a as QuizAtom, b as QuizAtom, c as QuizAtom])))

// Level 6 — length-4 chains. 6⁴ = 1296 enumerated candidates,
// runtime-filtered down to ~50 distinct results at chain-pool init.
// The component fetches all 1296 at BOTH ego genders, drops "self"
// results, dedupes by the (male-term, female-term) pair, and random-
// samples the pool size. Mount latency ~2-3s on staging (chunked
// 20-at-a-time over the bridge — see b0a1f98).
const ALL_L6: readonly QuizAtom[] = ['Father', 'Mother', 'Son', 'Daughter', 'Brother', 'Sister']

export const LEVEL_6_CANDIDATES: readonly Chain[] = ALL_L6
  .flatMap((a) => ALL_L6
    .flatMap((b) => ALL_L6
      .flatMap((c) => ALL_L6
        .map<Chain>((d) => [a, b, c, d]))))

export const LEVEL_6_SAMPLE_SIZE = 50

/**
 * Recognizer for engine "self" results. The production engine
 * returns "సొంత వ్యక్తి" in Telugu and "Self" in English when a
 * chain reduces back to ego. We filter these as quiz answers —
 * the player would see "self" as an option, which is both
 * confusing and unhelpful.
 *
 * Defensive: also catches empty / missing terms, which means
 * a label hasn't been fetched yet OR the chain isn't valid.
 */
export function isSelfTerm(term: string | undefined): boolean {
  if (!term) return true
  return term === 'సొంత వ్యక్తి' || term === 'Self'
}

/**
 * Drop any chain whose label lookup is missing or returns a
 * self term. Returned array preserves input order minus drops.
 *
 * `lookup` is keyed by chainKey(chain). Typical caller passes
 * the Map returned by useKinshipLabels.
 */
export function filterSelfLoops(
  candidates: readonly Chain[],
  lookup: ReadonlyMap<string, { term: string }>,
): Chain[] {
  return candidates.filter((c) => !isSelfTerm(lookup.get(chainKey(c))?.term))
}

/**
 * Convenience helper for commit 2 — returns the unfiltered
 * candidate pool for a given level. Filtering happens at the
 * component level once labels are fetched.
 */
export function candidatePoolFor(level: number): readonly Chain[] {
  switch (level) {
    case 1: return LEVEL_1_CHAINS
    case 2: return LEVEL_2_CANDIDATES
    case 3: return LEVEL_3_CANDIDATES
    case 4: return LEVEL_4_CANDIDATES
    case 5: return LEVEL_5_CANDIDATES
    case 6: return LEVEL_6_CANDIDATES
    // #quiz-l7-ui — L7 is server-curated (20 hand-picked entries
    // covering 4 tiers); the client never enumerates its candidates,
    // it just consumes the precomputed PoolEntry[] via useChainPool(7).
    // Returning [] here keeps the legacy-path validator (which still
    // exists for L1-L6 unit tests) from accidentally matching L7.
    default: return []
  }
}

// #kinship-quiz-unification commit 3 — ego-aware lookup key.
// Level 3+ requires distinct labels per ego gender (the same chain
// resolves differently). The lookup map is keyed on
// `${egoGender}|${chainKey}` so a single Map can hold both
// gender variants.
export type EgoLookupKey = string
export function egoKey(egoGender: 'male' | 'female', chain: Chain): EgoLookupKey {
  return `${egoGender}|${chainKey(chain)}`
}
